import { Typography, TypographyProps } from "@mui/material";
import DOMPurify from "dompurify";
import { escapeHtmlCharsInCodeBlock } from "helpers/CKEditor";
import hljs from "highlight.js";
import React, { useEffect, useMemo } from "react";

interface MarkdownFormatterProps extends TypographyProps {
  text: string;
}

const escapeTagToText = (input: string, tagName: string) =>
  input.replace(new RegExp(`<\\s*\\/?\\s*${tagName}\\b[^>]*>`, "gi"), (m) =>
    m.replace(/</g, "&lt;").replace(/>/g, "&gt;")
  );

const ESCAPE_TAGS = ["script", "iframe", "object", "embed", "svg", "math"];

const TextFormatter: React.FC<MarkdownFormatterProps> = ({ text, ...props }) => {
  const normalizedText = escapeHtmlCharsInCodeBlock(text.replace(/\\n/g, "\n"));
  const normalizedTextWithEscapedScript = ESCAPE_TAGS.reduce((acc, tag) => escapeTagToText(acc, tag), normalizedText);

  const sanitizedHtml = useMemo(
    () =>
      DOMPurify.sanitize(normalizedTextWithEscapedScript, {
        ALLOWED_TAGS: [
          "p",
          "br",
          "strong",
          "em",
          "u",
          "s",
          "ul",
          "ol",
          "li",
          "a",
          "img",
          "pre",
          "code",
          "blockquote",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "span",
          "div",
          "video",
          "audio",
        ],
        ALLOWED_ATTR: [
          "href",
          "src",
          "alt",
          "title",
          "target",
          "rel",
          "class",
          "style",
          "width",
          "height",
          "loading",
          "controls",
        ],
        FORBID_TAGS: ESCAPE_TAGS,
      }),
    [normalizedTextWithEscapedScript]
  );

  useEffect(() => {
    hljs.highlightAll();
  }, []);

  return (
    <Typography
      {...props}
      sx={{
        whiteSpace: "pre-wrap",
        "& pre": { whiteSpace: "pre-wrap" },
        "& img, & video": { height: "auto" },
        "& img, & video, & audio": { maxWidth: "100%", display: "block" },
        "& img": { margin: "1em auto" },
        "& video": { padding: "1em" },
        "& audio": { margin: "1em" },
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

export default TextFormatter;
